import { useEffect, useRef } from 'react';
import { StatusBadge, cn } from '@immo/ui-kit';
import type { FleetJobView, FleetMachineOverview, FleetProfileStatus } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { ServerIcon } from '@/components/layout/icons';
import { FleetApprovalBadge, FleetStateCircle, STATE_LABEL_CLASS } from './fleet-badges';
import { SuspectBadge } from './job-pulse';
import { TileHealthRow } from './health/tile-health-row';
import { FleetThrottleBadge } from './health/throttle-badge';

type Props = {
  readonly pending: ReadonlyArray<FleetMachineOverview>;
  readonly machines: ReadonlyArray<FleetMachineOverview>;
  readonly selectedId: string | null;
  readonly onSelect: (machineId: string) => void;
  /** The fleet's live jobs; a machine holding a suspect one says so on its tile (fleet-agent ADR 0012). */
  readonly jobs?: ReadonlyArray<FleetJobView>;
};

/**
 * The fleet as machine tiles (OPS-67): one per row, the pending group always
 * on top. Each tile carries one lane tile per lane,
 * whose circle is the lane's state — the thing an operator scans the page for.
 * Approved tiles also carry the health row and the throttle badge (IMM-1450).
 *
 * Carries NO page title of its own: the topbar already reads `Fleet <n>`.
 */
export function MachineGrid({ pending, machines, selectedId, onSelect, jobs = [] }: Props) {
  const t = useTranslations('pages.admin.fleet');

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      {pending.length > 0 && (
        <GridGroup label={t('group-pending')} count={pending.length} pending>
          {pending.map((machine) => (
            <MachineTile
              key={machine.id}
              machine={machine}
              selected={machine.id === selectedId}
              onSelect={onSelect}
              suspects={jobs.filter((job) => job.machineId === machine.id && job.suspect !== null)}
            />
          ))}
        </GridGroup>
      )}
      <GridGroup label={t('group-machines')} count={machines.length}>
        {machines.map((machine) => (
          <MachineTile
            key={machine.id}
            machine={machine}
            selected={machine.id === selectedId}
            onSelect={onSelect}
            suspects={jobs.filter((job) => job.machineId === machine.id && job.suspect !== null)}
          />
        ))}
      </GridGroup>
    </div>
  );
}

function GridGroup({
  label,
  count,
  pending = false,
  children,
}: {
  readonly label: string;
  readonly count: number;
  readonly pending?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            pending ? 'text-warning-700' : 'text-text-tertiary',
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-xs tabular-nums',
            pending ? 'bg-warning-100 text-warning-700' : 'bg-neutral-100 text-text-tertiary',
          )}
        >
          {count}
        </span>
      </div>
      <div className="grid grid-cols-1 items-start gap-4">{children}</div>
    </section>
  );
}

function MachineTile({
  machine,
  selected,
  onSelect,
  suspects,
}: {
  readonly machine: FleetMachineOverview;
  readonly selected: boolean;
  readonly onSelect: (machineId: string) => void;
  /** Its live jobs a review judged stuck — loud on the tile; the actions live on the lane card in the panel. */
  readonly suspects: ReadonlyArray<FleetJobView>;
}) {
  const t = useTranslations('pages.admin.fleet');
  const tJobs = useTranslations('pages.admin.jobs');
  const format = useLocaleFormat();
  const ref = useRef<HTMLButtonElement>(null);
  const isPending = machine.approval === 'pending';

  // A deep link can name a machine far down the grid; bring it into view so
  // the tile the panel belongs to is the one the operator can see.
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onSelect(machine.id)}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'relative flex min-w-0 flex-col gap-3 overflow-hidden rounded-lg border p-4 text-left shadow-sm',
        selected
          ? 'border-interactive bg-selected-bg'
          : isPending
            ? 'border-warning-200 bg-warning-50'
            : 'border-border bg-surface',
      )}
    >
      {machine.stale && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-danger-500" />}

      <div className="flex min-w-0 flex-col gap-1.5">
        {/* Below `sm` the name owns its row and the badges wrap under it: a
            phone has room for one of them, and the name is the one to keep. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1">
            <ServerIcon
              className={cn('h-4 w-4 shrink-0', machine.stale ? 'text-danger-500' : 'text-text-tertiary')}
              aria-hidden
            />
            <span className="min-w-0 truncate text-base font-semibold text-text-primary">{machine.name}</span>
          </span>
          {machine.approval !== 'approved' && <FleetApprovalBadge approval={machine.approval} />}
          {machine.stale && <StatusBadge label={t('stale')} tone="danger" dot />}
          {suspects.length > 0 && <SuspectBadge />}
          {machine.approval === 'approved' && machine.health?.throttle != null && (
            <FleetThrottleBadge throttle={machine.health.throttle} />
          )}
        </div>

        <div className="flex min-w-0 items-center gap-2 pl-6 text-sm text-text-tertiary">
          <span className="min-w-0 truncate font-mono">{machine.hostname}</span>
          <span className="shrink-0 text-text-placeholder">·</span>
          <span className={cn('shrink-0', machine.stale && 'font-medium text-danger-700')}>
            {format.ago(machine.lastSeenAt)}
          </span>
          <span className="hidden shrink-0 text-text-placeholder sm:inline">·</span>
          <span className="hidden shrink-0 font-mono sm:inline">{machine.platform}</span>
        </div>
      </div>

      {machine.approval === 'approved' && <TileHealthRow health={machine.health ?? null} stale={machine.stale} />}

      {suspects.map((job) =>
        job.suspect === null ? null : (
          <span
            key={job.id}
            className="flex min-w-0 flex-col gap-0.5 rounded-md bg-warning-50 px-3 py-2 text-sm text-warning-700"
          >
            <span className="truncate font-mono font-medium">
              {job.worker} · {job.key}
            </span>
            <span className="break-words">
              {tJobs('suspect-note', {
                time: format.ago(job.suspect.since),
                reason: job.suspect.reason,
              })}
            </span>
          </span>
        ),
      )}

      {machine.profiles.length > 0 ? (
        // At least 150px a lane, at most 8 a row: a track never drops below an
        // eighth of the row (minus the 7 gaps).
        <div
          className={cn(
            'grid grid-cols-[repeat(auto-fit,minmax(max(150px,calc((100%_-_3.5rem)/8)),1fr))] gap-2',
            machine.stale && 'opacity-50',
          )}
        >
          {machine.profiles.map((profile) => (
            <LaneTile key={profile.profile} profile={profile} />
          ))}
        </div>
      ) : (
        <div className="text-sm text-text-placeholder">{t('no-profiles')}</div>
      )}
    </button>
  );
}

/** One lane: the state circle, the lane name, the translated state, the agent's title and the running round's engine. Display only. */
function LaneTile({ profile }: { readonly profile: FleetProfileStatus }) {
  const t = useTranslations('pages.admin.fleet.state');
  const tEngine = useTranslations('pages.admin.fleet.engine');
  const quiet = profile.state === 'stopped' || profile.state === 'pending';

  return (
    <span
      className={cn(
        'flex min-w-0 flex-col gap-1 rounded-md border px-3 py-2',
        profile.state === 'pending' ? 'border-dashed border-neutral-300' : 'border-border',
        quiet ? 'bg-neutral-50' : 'bg-surface',
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <FleetStateCircle state={profile.state} />
        <span className="min-w-0 truncate font-mono text-base-sm font-medium text-text-primary">{profile.profile}</span>
      </span>
      <span className={cn('truncate pl-5 text-sm font-medium', STATE_LABEL_CLASS[profile.state])}>
        {t(profile.state)}
      </span>
      <span
        className={cn(
          'hidden truncate pl-5 text-sm sm:block',
          profile.title !== null ? 'text-text-secondary' : 'text-text-placeholder',
        )}
      >
        {profile.title ?? '—'}
      </span>
      {profile.engineRotation?.running != null && (
        <span className="truncate pl-5 text-sm text-text-secondary">
          {tEngine('running', { engine: profile.engineRotation.running })}
        </span>
      )}
    </span>
  );
}
